<template>
  <v-dialog
    :value="value"
    max-width="640px"
    :fullscreen="$vuetify.breakpoint.xsOnly"
    scrollable
    @input="$emit('input', $event)"
  >
    <v-card class="rounded-xl d-flex flex-column" style="max-height: 90vh">
      <!-- Title header -->
      <v-card-title class="primary white--text py-3 d-flex align-center">
        <v-icon color="white" class="mr-2">mdi-brain</v-icon>
        <div class="text-truncate">
          <span class="text-subtitle-1 text-sm-h6 font-weight-bold white--text">
            FPSS-aivohalvausmittari
          </span>
          <span
            class="d-none d-sm-inline text-caption white--text text--lighten-3 ml-2"
          >
            (Finnish Prehospital Stroke Scale)
          </span>
        </div>
        <v-spacer />
        <v-chip
          small
          color="white"
          text-color="primary"
          class="font-weight-bold mr-2"
        >
          {{ totalScoreText }}
        </v-chip>
        <v-btn icon dark @click="$emit('input', false)">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <!-- Step Navigation Bar / Wizard Steps -->
      <div :class="$vuetify.theme.dark ? 'grey darken-4' : 'grey lighten-4'" class="px-2 py-2 border-bottom">
        <v-row no-gutters class="text-center">
          <v-col v-for="(st, idx) in steps" :key="idx" cols="2" class="px-1">
            <v-btn
              block
              x-small
              depressed
              :color="currentStep === idx + 1 ? 'primary' : 'white'"
              :class="[
                'rounded-pill text-none font-weight-bold px-1',
                currentStep === idx + 1
                  ? 'white--text'
                  : 'grey--text text--darken-3',
              ]"
              @click="currentStep = idx + 1"
            >
              <span class="text-truncate">{{ st.short }}</span>
              <v-badge
                v-if="st.score !== null"
                inline
                x-small
                :content="`${st.score}p`"
                :color="
                  currentStep === idx + 1 ? 'red darken-4' : 'grey lighten-1'
                "
                class="ml-1 d-none d-sm-inline-flex"
              />
            </v-btn>
          </v-col>
        </v-row>
      </div>

      <!-- Scrollable Step Content -->
      <v-card-text class="pt-4 px-3 px-sm-5 flex-grow-1 overflow-y-auto">
        <!-- Quick shortcut: all normal / 0 points -->
        <div
          v-if="currentStep < 6 && !isAllZero"
          class="mb-3 d-flex justify-end"
        >
          <v-btn
            x-small
            outlined
            color="primary"
            class="font-weight-bold rounded-pill text-none"
            @click="setAllZero"
          >
            <v-icon x-small left>mdi-flash</v-icon>
            Pika-asetus: Täysin oireeton (0 p)
          </v-btn>
        </div>

        <!-- STEP 1: KASVOT -->
        <div v-if="currentStep === 1">
          <div class="d-flex align-center mb-1">
            <v-avatar color="primary lighten-5" size="32" class="mr-2">
              <v-icon color="primary" small
                >mdi-emoticon-neutral-outline</v-icon
              >
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold primary--text">
                1/5 Suupieli / Kasvot
              </div>
              <div class="text-caption grey--text text--darken-2">
                {{ criteria.face.desc }}
              </div>
            </div>
          </div>

          <v-divider class="my-3" />

          <div class="d-flex flex-column" style="gap: 10px">
            <v-card
              v-for="opt in criteria.face.options"
              :key="opt.score"
              outlined
              hover
              :class="[
                'pa-3 rounded-lg cursor-pointer transition-swing',
                faceScore === opt.score
                  ? 'red lighten-5 primary--border font-weight-bold'
                  : 'white',
              ]"
              :style="
                faceScore === opt.score
                  ? 'border: 2px solid #6c0000 !important;'
                  : ''
              "
              @click="selectOption('face', opt.score)"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="36"
                  :color="
                    faceScore === opt.score ? 'primary' : 'grey lighten-3'
                  "
                  :class="
                    faceScore === opt.score
                      ? 'white--text font-weight-bold mr-3'
                      : 'grey--text text--darken-3 font-weight-bold mr-3'
                  "
                >
                  {{ opt.score }} p
                </v-avatar>
                <div class="flex-grow-1">
                  <div
                    class="text-subtitle-2 font-weight-bold"
                    :class="
                      faceScore === opt.score ? 'primary--text' : 'black--text'
                    "
                  >
                    {{ opt.title }}
                  </div>
                  <div class="text-caption grey--text text--darken-2">
                    {{ opt.desc }}
                  </div>
                </div>
                <v-icon
                  v-if="faceScore === opt.score"
                  color="primary"
                  class="ml-2"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </v-card>
          </div>
        </div>

        <!-- STEP 2: RAAJAT -->
        <div v-else-if="currentStep === 2">
          <div class="d-flex align-center mb-1">
            <v-avatar color="primary lighten-5" size="32" class="mr-2">
              <v-icon color="primary" small>mdi-hand-back-right</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold primary--text">
                2/5 Raajojen voima
              </div>
              <div class="text-caption grey--text text--darken-2">
                {{ criteria.arms.desc }}
              </div>
            </div>
          </div>

          <v-divider class="my-3" />

          <div class="d-flex flex-column" style="gap: 10px">
            <v-card
              v-for="opt in criteria.arms.options"
              :key="opt.score"
              outlined
              hover
              :class="[
                'pa-3 rounded-lg cursor-pointer transition-swing',
                armsScore === opt.score
                  ? 'red lighten-5 primary--border font-weight-bold'
                  : 'white',
              ]"
              :style="
                armsScore === opt.score
                  ? 'border: 2px solid #6c0000 !important;'
                  : ''
              "
              @click="selectOption('arms', opt.score)"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="36"
                  :color="
                    armsScore === opt.score ? 'primary' : 'grey lighten-3'
                  "
                  :class="
                    armsScore === opt.score
                      ? 'white--text font-weight-bold mr-3'
                      : 'grey--text text--darken-3 font-weight-bold mr-3'
                  "
                >
                  {{ opt.score }} p
                </v-avatar>
                <div class="flex-grow-1">
                  <div
                    class="text-subtitle-2 font-weight-bold"
                    :class="
                      armsScore === opt.score ? 'primary--text' : 'black--text'
                    "
                  >
                    {{ opt.title }}
                  </div>
                  <div class="text-caption grey--text text--darken-2">
                    {{ opt.desc }}
                  </div>
                </div>
                <v-icon
                  v-if="armsScore === opt.score"
                  color="primary"
                  class="ml-2"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </v-card>
          </div>
        </div>

        <!-- STEP 3: PUHE -->
        <div v-else-if="currentStep === 3">
          <div class="d-flex align-center mb-1">
            <v-avatar color="primary lighten-5" size="32" class="mr-2">
              <v-icon color="primary" small>mdi-account-voice</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold primary--text">
                3/5 Puhe (Kielellinen vaste)
              </div>
              <div class="text-caption grey--text text--darken-2">
                {{ criteria.speech.desc }}
              </div>
            </div>
          </div>

          <v-divider class="my-3" />

          <div class="d-flex flex-column" style="gap: 10px">
            <v-card
              v-for="opt in criteria.speech.options"
              :key="opt.score"
              outlined
              hover
              :class="[
                'pa-3 rounded-lg cursor-pointer transition-swing',
                speechScore === opt.score
                  ? 'red lighten-5 primary--border font-weight-bold'
                  : 'white',
              ]"
              :style="
                speechScore === opt.score
                  ? 'border: 2px solid #6c0000 !important;'
                  : ''
              "
              @click="selectOption('speech', opt.score)"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="36"
                  :color="
                    speechScore === opt.score ? 'primary' : 'grey lighten-3'
                  "
                  :class="
                    speechScore === opt.score
                      ? 'white--text font-weight-bold mr-3'
                      : 'grey--text text--darken-3 font-weight-bold mr-3'
                  "
                >
                  {{ opt.score }} p
                </v-avatar>
                <div class="flex-grow-1">
                  <div
                    class="text-subtitle-2 font-weight-bold"
                    :class="
                      speechScore === opt.score
                        ? 'primary--text'
                        : 'black--text'
                    "
                  >
                    {{ opt.title }}
                  </div>
                  <div class="text-caption grey--text text--darken-2">
                    {{ opt.desc }}
                  </div>
                </div>
                <v-icon
                  v-if="speechScore === opt.score"
                  color="primary"
                  class="ml-2"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </v-card>
          </div>
        </div>

        <!-- STEP 4: NÄKÖ -->
        <div v-else-if="currentStep === 4">
          <div class="d-flex align-center mb-1">
            <v-avatar color="primary lighten-5" size="32" class="mr-2">
              <v-icon color="primary" small>mdi-eye</v-icon>
            </v-avatar>
            <div>
              <div class="text-subtitle-1 font-weight-bold primary--text">
                4/5 Näkö (Näkökenttäpuutos / Kaksoiskuvat)
              </div>
              <div class="text-caption grey--text text--darken-2">
                {{ criteria.vision.desc }}
              </div>
            </div>
          </div>

          <v-divider class="my-3" />

          <div class="d-flex flex-column" style="gap: 10px">
            <v-card
              v-for="opt in criteria.vision.options"
              :key="opt.score"
              outlined
              hover
              :class="[
                'pa-3 rounded-lg cursor-pointer transition-swing',
                visionScore === opt.score
                  ? 'red lighten-5 primary--border font-weight-bold'
                  : 'white',
              ]"
              :style="
                visionScore === opt.score
                  ? 'border: 2px solid #6c0000 !important;'
                  : ''
              "
              @click="selectOption('vision', opt.score)"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="36"
                  :color="
                    visionScore === opt.score ? 'primary' : 'grey lighten-3'
                  "
                  :class="
                    visionScore === opt.score
                      ? 'white--text font-weight-bold mr-3'
                      : 'grey--text text--darken-3 font-weight-bold mr-3'
                  "
                >
                  {{ opt.score }} p
                </v-avatar>
                <div class="flex-grow-1">
                  <div
                    class="text-subtitle-2 font-weight-bold"
                    :class="
                      visionScore === opt.score
                        ? 'primary--text'
                        : 'black--text'
                    "
                  >
                    {{ opt.title }}
                  </div>
                  <div class="text-caption grey--text text--darken-2">
                    {{ opt.desc }}
                  </div>
                </div>
                <v-icon
                  v-if="visionScore === opt.score"
                  color="primary"
                  class="ml-2"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </v-card>
          </div>
        </div>

        <!-- STEP 5: KATSEEN / PÄÄN DEVIAATIO -->
        <div v-else-if="currentStep === 5">
          <div class="d-flex align-center mb-1">
            <v-avatar color="red lighten-5" size="32" class="mr-2">
              <v-icon color="red darken-4" small>mdi-compass</v-icon>
            </v-avatar>
            <div>
              <div
                class="text-subtitle-1 font-weight-bold red--text text--darken-4"
              >
                5/5 Katseen tai pään suunnan poikkeama (Deviaatio)
              </div>
              <div class="text-caption grey--text text--darken-2">
                {{ criteria.gaze.desc }}
              </div>
            </div>
          </div>

          <v-alert
            dense
            outlined
            color="red darken-3"
            class="my-3 text-caption rounded-lg"
          >
            <strong>Tärkeä huomio:</strong> Katseen tai pään pysyvä kääntyminen
            sivulle antaa <strong>4 pistettä</strong>. Se on ensihoidossa vahvin
            yksittäinen suuren aivovaltimon tukoksen (LVO) merkki ja viittaa
            suoraan mekaanisen trombektomian tarpeeseen!
          </v-alert>

          <div class="d-flex flex-column" style="gap: 10px">
            <v-card
              v-for="opt in criteria.gaze.options"
              :key="opt.score"
              outlined
              hover
              :class="[
                'pa-3 rounded-lg cursor-pointer transition-swing',
                gazeScore === opt.score
                  ? 'red lighten-5 primary--border font-weight-bold'
                  : 'white',
              ]"
              :style="
                gazeScore === opt.score
                  ? 'border: 2px solid #6c0000 !important;'
                  : ''
              "
              @click="selectOption('gaze', opt.score)"
            >
              <div class="d-flex align-center">
                <v-avatar
                  size="36"
                  :color="
                    gazeScore === opt.score
                      ? opt.score === 4
                        ? 'red darken-4'
                        : 'primary'
                      : 'grey lighten-3'
                  "
                  :class="
                    gazeScore === opt.score
                      ? 'white--text font-weight-bold mr-3'
                      : 'grey--text text--darken-3 font-weight-bold mr-3'
                  "
                >
                  {{ opt.score }} p
                </v-avatar>
                <div class="flex-grow-1">
                  <div
                    class="text-subtitle-2 font-weight-bold"
                    :class="
                      gazeScore === opt.score
                        ? opt.score === 4
                          ? 'red--text text--darken-4'
                          : 'primary--text'
                        : 'black--text'
                    "
                  >
                    {{ opt.title }}
                  </div>
                  <div class="text-caption grey--text text--darken-2">
                    {{ opt.desc }}
                  </div>
                </div>
                <v-icon
                  v-if="gazeScore === opt.score"
                  color="primary"
                  class="ml-2"
                >
                  mdi-check-circle
                </v-icon>
              </div>
            </v-card>
          </div>
        </div>

        <!-- STEP 6: TULOS JA TOIMINTAOHJEET -->
        <div v-else-if="currentStep === 6">
          <!-- Big Score Banner -->
          <v-card
            flat
            :class="[
              'pa-4 rounded-xl text-center mb-4',
              interpretation.severityBgClass,
            ]"
            style="border: 2px solid #6c0000 !important"
          >
            <div
              class="text-caption text-uppercase font-weight-bold letter-spacing-1"
            >
              Finnish Prehospital Stroke Scale (FPSS)
            </div>
            <div
              class="text-h3 font-weight-black my-1"
              :class="interpretation.severityTextClass"
            >
              {{ totalScore }} / 8 p
            </div>
            <v-chip
              :color="interpretation.severityChipColor"
              text-color="white"
              class="font-weight-bold text-caption text-sm-subtitle-2 px-3 my-1"
            >
              {{ interpretation.tag }}
            </v-chip>
            <div
              class="text-body-2 font-weight-bold grey--text text--darken-4 mt-2"
            >
              {{ interpretation.subTitle }}
            </div>
            <div class="text-caption mt-1 grey--text text--darken-3 font-mono">
              Merkintä raporttiin:
              <strong
                >FPSS {{ totalScore }}/8 p (K{{ faceScore }} R{{
                  armsScore
                }}
                P{{ speechScore }} N{{ visionScore }} Katse{{
                  gazeScore
                }})</strong
              >
            </div>
          </v-card>

          <!-- Component Breakdown Summary -->
          <div class="text-subtitle-2 font-weight-bold mb-2">
            Pisteiden erittely:
          </div>
          <v-row dense class="mb-3">
            <v-col
              v-for="item in breakdownSummary"
              :key="item.key"
              cols="6"
              sm="4"
            >
              <v-card
                outlined
                class="pa-2 rounded-lg text-center h-100 grey lighten-5 cursor-pointer"
                @click="currentStep = item.step"
              >
                <div class="text-caption grey--text text--darken-1">
                  {{ item.name }}
                </div>
                <div class="text-subtitle-2 font-weight-bold primary--text">
                  {{ item.score !== null ? item.score : 0 }} / {{ item.max }} p
                </div>
                <div class="text-caption text-truncate">
                  {{ item.title }}
                </div>
              </v-card>
            </v-col>
          </v-row>

          <!-- Action Recommendations Based on Score -->
          <v-card
            outlined
            class="pa-3 rounded-lg mb-3"
            :class="interpretation.severityBorderClass"
          >
            <div class="d-flex align-center mb-2">
              <v-icon :color="interpretation.severityIconColor" class="mr-2">
                {{ interpretation.severityIcon }}
              </v-icon>
              <div
                class="text-subtitle-2 font-weight-bold"
                :class="interpretation.severityTextClass"
              >
                Ensivasteen toimintaohje (FPSS {{ totalScore }} p):
              </div>
            </div>

            <p class="text-body-2 grey--text text--darken-3 mb-2">
              {{ interpretation.summary }}
            </p>

            <v-list dense class="pa-0 transparent">
              <v-list-item
                v-for="(stepText, sIdx) in interpretation.actionSteps"
                :key="sIdx"
                class="px-2 py-1 rounded mb-1 white"
                style="border-left: 3px solid #6c0000"
              >
                <v-list-item-content class="py-0">
                  <span
                    class="text-caption font-weight-medium grey--text text--darken-4"
                  >
                    {{ stepText }}
                  </span>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </v-card>

          <!-- Copy Button -->
          <div class="d-flex justify-end mt-2">
            <v-btn
              text
              small
              color="primary"
              class="font-weight-bold"
              @click="copyReportText"
            >
              <v-icon left small>{{
                copied ? "mdi-check" : "mdi-content-copy"
              }}</v-icon>
              {{ copied ? "Kopioitu leikepöydälle!" : "Kopioi merkintä" }}
            </v-btn>
          </div>
        </div>
      </v-card-text>

      <!-- Sticky Footer Actions -->
      <v-divider />
      <v-card-actions class="pa-3 grey lighten-4 d-flex">
        <v-btn
          v-if="currentStep > 1"
          text
          small
          color="grey darken-2"
          class="font-weight-bold"
          @click="currentStep--"
        >
          <v-icon left small>mdi-arrow-left</v-icon>
          Edellinen
        </v-btn>

        <v-btn
          text
          small
          color="red darken-2"
          class="font-weight-bold"
          @click="resetAll"
        >
          <v-icon left small>mdi-refresh</v-icon>
          Tyhjennä
        </v-btn>

        <v-spacer />

        <v-btn
          v-if="currentStep < 6"
          color="primary"
          small
          depressed
          class="font-weight-bold"
          @click="nextStep"
        >
          {{ currentStep === 5 ? "Näytä tulos" : "Seuraava" }}
          <v-icon right small>mdi-arrow-right</v-icon>
        </v-btn>
        <v-btn
          v-else
          color="primary"
          small
          depressed
          class="font-weight-bold"
          @click="$emit('input', false)"
        >
          Valmis / Sulje
          <v-icon right small>mdi-check</v-icon>
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { FPSS_CRITERIA, interpretFpss } from "@/assets/ensivasteOhjeet";

export default {
  name: "FpssWizard",
  props: {
    value: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentStep: 1,
      faceScore: null,
      armsScore: null,
      speechScore: null,
      visionScore: null,
      gazeScore: null,
      copied: false,
      criteria: FPSS_CRITERIA,
    };
  },
  computed: {
    steps() {
      return [
        { short: "1. Kasvot", score: this.faceScore },
        { short: "2. Raajat", score: this.armsScore },
        { short: "3. Puhe", score: this.speechScore },
        { short: "4. Näkö", score: this.visionScore },
        { short: "5. Katse", score: this.gazeScore },
        { short: "6. Tulos", score: this.totalScore },
      ];
    },
    totalScore() {
      const f = this.faceScore || 0;
      const a = this.armsScore || 0;
      const s = this.speechScore || 0;
      const v = this.visionScore || 0;
      const g = this.gazeScore || 0;
      return f + a + s + v + g;
    },
    totalScoreText() {
      return `FPSS: ${this.totalScore} / 8 p`;
    },
    interpretation() {
      return interpretFpss(this.totalScore);
    },
    isAllZero() {
      return (
        this.faceScore === 0 &&
        this.armsScore === 0 &&
        this.speechScore === 0 &&
        this.visionScore === 0 &&
        this.gazeScore === 0
      );
    },
    breakdownSummary() {
      return [
        {
          key: "face",
          name: "Kasvot",
          score: this.faceScore,
          max: 1,
          step: 1,
          title: this.faceScore === 1 ? "Puoliero" : "Normaali",
        },
        {
          key: "arms",
          name: "Raajat",
          score: this.armsScore,
          max: 1,
          step: 2,
          title: this.armsScore === 1 ? "Heikkous" : "Normaali",
        },
        {
          key: "speech",
          name: "Puhe",
          score: this.speechScore,
          max: 1,
          step: 3,
          title: this.speechScore === 1 ? "Häiriö" : "Normaali",
        },
        {
          key: "vision",
          name: "Näkö",
          score: this.visionScore,
          max: 1,
          step: 4,
          title: this.visionScore === 1 ? "Puutos/Kuvat" : "Normaali",
        },
        {
          key: "gaze",
          name: "Katse",
          score: this.gazeScore,
          max: 4,
          step: 5,
          title: this.gazeScore === 4 ? "Deviaatio (LVO!)" : "Normaali",
        },
      ];
    },
  },
  watch: {
    value(val) {
      if (val && this.faceScore === null) {
        this.currentStep = 1;
      }
    },
  },
  methods: {
    selectOption(category, score) {
      if (category === "face") {
        this.faceScore = score;
        this.currentStep = 2;
      } else if (category === "arms") {
        this.armsScore = score;
        this.currentStep = 3;
      } else if (category === "speech") {
        this.speechScore = score;
        this.currentStep = 4;
      } else if (category === "vision") {
        this.visionScore = score;
        this.currentStep = 5;
      } else if (category === "gaze") {
        this.gazeScore = score;
        this.currentStep = 6;
      }
    },
    nextStep() {
      if (this.currentStep < 6) {
        this.currentStep++;
      }
    },
    setAllZero() {
      this.faceScore = 0;
      this.armsScore = 0;
      this.speechScore = 0;
      this.visionScore = 0;
      this.gazeScore = 0;
      this.currentStep = 6;
    },
    resetAll() {
      this.faceScore = null;
      this.armsScore = null;
      this.speechScore = null;
      this.visionScore = null;
      this.gazeScore = null;
      this.currentStep = 1;
    },
    copyReportText() {
      const text = `FPSS: ${this.totalScore}/8 p (Kasvot ${
        this.faceScore || 0
      }, Raajat ${this.armsScore || 0}, Puhe ${this.speechScore || 0}, Näkö ${
        this.visionScore || 0
      }, Katse ${this.gazeScore || 0}) - ${this.interpretation.title}`;
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(text);
        this.copied = true;
        setTimeout(() => {
          this.copied = false;
        }, 2500);
      }
    },
  },
};
</script>

<style scoped>
.border-bottom {
  border-bottom: 1px solid rgba(0, 0, 0, 0.08);
}
.cursor-pointer {
  cursor: pointer;
}
.primary--border {
  border-color: #6c0000 !important;
}
.red--border {
  border-color: #c62828 !important;
}
.orange--border {
  border-color: #ef6c00 !important;
}
.green--border {
  border-color: #2e7d32 !important;
}
.letter-spacing-1 {
  letter-spacing: 1px;
}
</style>
